import React, { useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { getIssuerOrganizations } from '~/javascripts/admin/actions/organization';
import { toQueryParams } from '~/javascripts/admin/utils';
import { Button } from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Select from '../../atoms/Select';
import Table from '../../atoms/Table';
import TextBox from '../../atoms/TextBox';
import BreadCrumb from '../../molecules/BreadCrumb';
import LabeledSelect from '../../molecules/LabeledSelect';
import Pager from '../../shared/Pager';

type GetIssuerOrganizationResponse = Awaited<ReturnType<typeof getIssuerOrganizations>>;
type IssuerOrganization = GetIssuerOrganizationResponse['rows'][number];

const Section = styled.section`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;

  ${Label} {
    width: 100px;
  }
  ${Select as any} {
    width: 300px;
  }
  ${TextBox} {
    width: 300px;
  }
`;

const SectionContent = styled.div``;

const SearchResultInfo = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;
const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  padding: 16px 0;
`;
const SearchResultCount = styled.span`
  flex: 1;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: 'サービス管理', href: '', isCurrentPage: true },
  { value: '発行体管理', href: '', isCurrentPage: true },
];

const PER_PAGE = 50;

const IssuerOrganizations = () => {
  const history = useHistory();
  const [organizations, setOrganizations] = React.useState<IssuerOrganization[]>([]);
  const [filteredOrganizations, setFilteredOrganizations] = React.useState<IssuerOrganization[]>(
    []
  );
  const [pagination, setPagination] = React.useState<
    GetIssuerOrganizationResponse['pagination'] | undefined
  >(undefined);
  const [selectedOrganization, setSelectedOrganization] = React.useState<{
    label: string;
    value: IssuerOrganization;
  } | null>(null);

  useEffect(() => {
    getIssuerOrganizations(toQueryParams({ perPage: 10000 })).then((response) => {
      setOrganizations(response.rows);
      setFilteredOrganizations(response.rows);
      setPagination({
        ...response.pagination,
        per_page: PER_PAGE,
        max_page: Math.ceil(response.rows.length / PER_PAGE),
        has_next: response.rows.length > PER_PAGE,
      });
    });
  }, []);

  const onChangePage = (page: number) => {
    if (pagination) {
      setPagination({
        ...pagination,
        current: page,
        has_next: page < pagination.max_page,
        has_prev: page > 1,
      });
    }
  };

  useEffect(() => {
    const filtered = organizations.filter(
      (organization) =>
        !selectedOrganization || organization.code === selectedOrganization?.value.code
    );
    setFilteredOrganizations(filtered);
    setPagination((prev) => ({
      ...prev,
      max_page: Math.ceil(filtered.length / PER_PAGE),
      has_next: filtered.length > PER_PAGE,
      current: 1,
      has_prev: false,
      per_page: PER_PAGE,
    }));
  }, [selectedOrganization, organizations]);

  const paginatedOrganizations = filteredOrganizations.slice(
    ((pagination?.current ?? 1) - 1) * PER_PAGE,
    (pagination?.current || 1) * PER_PAGE
  );

  const organizationOptions = organizations.map((organization) => ({
    value: organization,
    label: `${organization.name} (${organization.code})`,
  }));

  return (
    <>
      <BreadCrumb crumbs={CRUMBS} />
      <Section>
        <Headings.h5>発行体管理</Headings.h5>
        <SectionContent>
          <Button onClick={() => history.push('/service-settings/issuer-organization/create')}>
            新規作成
          </Button>
        </SectionContent>
      </Section>
      <Section>
        <Headings.h5>検索条件</Headings.h5>
        <LabeledSelect
          isMulti={false}
          labelText="組織"
          options={organizationOptions}
          onChange={(option) => setSelectedOrganization(option)}
          value={selectedOrganization}
          isClearable
        />
      </Section>
      <Section>
        <SearchResultInfo>
          <SearchResultTitle>検索結果</SearchResultTitle>
          <SearchResultCount>件数：{filteredOrganizations?.length ?? 0}</SearchResultCount>
        </SearchResultInfo>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Cell size="3" align="center">
                組織コード
              </Table.Cell>
              <Table.Cell size="3" align="center">
                組織名
              </Table.Cell>
              <Table.Cell size="6" align="center">
                登録番号
              </Table.Cell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {paginatedOrganizations.map((organization) => (
              <Table.Row key={organization.code}>
                <Table.Cell align="center">
                  <Link
                    to={`/service-settings/issuer-organization/${encodeURIComponent(
                      organization.code
                    )}`}
                  >
                    {organization.code}
                  </Link>
                </Table.Cell>
                <Table.Cell align="center" wordBreak="break-word">
                  {organization.name}
                </Table.Cell>
                <Table.Cell align="center">{organization.invoice_registration_number}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row noBorder>
              <Table.Cell colSpan={3} align="center" styles={{ paddingTop: '16px' }}>
                {pagination && <Pager pagination={pagination} onChangePage={onChangePage} />}
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </Section>
    </>
  );
};

export default IssuerOrganizations;
